import {
  Building2,
  Calendar,
  CheckCircle2,
  Circle,
  ClipboardList,
  MapPin,
  Phone,
  Stethoscope,
  Users,
} from "lucide-react";
import { DailyTaskDwo, Followup, OrgVisit } from "./types/dailyTaskDwo";
import { motion } from "framer-motion";
import { useCalcWeitage } from "./hooks/useWeitage";

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const StatTile = ({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  icon: React.ElementType;
}) => (
  <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
    <div className="bg-gray-50 rounded-lg p-2">
      <Icon className="size-4 text-gray-500" />
    </div>
    <div>
      <p className="text-lg font-semibold text-gray-900 leading-none">
        {value}
      </p>
      <p className="text-[11px] text-gray-400 mt-1">{label}</p>
    </div>
  </div>
);

const MembershipBreakdown = ({ task }: { task: DailyTaskDwo }) => {
  const tiers = [
    {
      label: "Silver",
      value: task.silver_members_registered + task.silver_members_registered_12,
      color: "bg-zinc-100 text-zinc-600",
    },
    {
      label: "Gold",
      value: task.gold_members_registered + task.gold_members_registered_12,
      color: "bg-yellow-50 text-yellow-600",
    },
    {
      label: "Platinum",
      value:
        task.platinum_members_registered + task.platinum_members_registered_12,
      color: "bg-slate-200 text-slate-600",
    },
    {
      label: "Star",
      value: task.star_members_registered + task.star_members_registered_12,
      color: "bg-amber-100 text-amber-600",
    },
    {
      label: "Diamond",
      value:
        task.diamond_members_registered + task.diamond_members_registered_12,
      color: "bg-fuchsia-50 text-fuchsia-600",
    },
  ];

  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">
        Membership Subscriptions
      </p>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {tiers.map((tier) => (
          <div
            key={tier.label}
            className={`rounded-xl p-3 text-center ${tier.color}`}
          >
            <p className="text-xl font-semibold leading-none">{tier.value}</p>
            <p className="text-[10px] uppercase tracking-wide mt-1">
              {tier.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const FollowupCard = ({ followup }: { followup: Followup }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm space-y-1.5"
  >
    <div className="flex items-start justify-between gap-2">
      <p className="text-sm font-medium text-gray-900">{followup.name}</p>
      <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 shrink-0">
        {followup.entity_type}
      </span>
    </div>
    <p className="text-xs text-gray-500">{followup.outcome_details}</p>
    <div className="flex items-center gap-3 text-[11px] text-gray-400 pt-1">
      <span className="flex items-center gap-1">
        <Users className="size-3" />
        {followup.expected_participants} expected
      </span>

      {followup.next_followup_date && (
        <span className="flex items-center gap-1">
          <Calendar className="size-3" />
          Next Followup: {formatDate(followup.next_followup_date)}
        </span>
      )}
    </div>
  </motion.div>
);

const OrgVisitCard = ({ visit }: { visit: OrgVisit }) => {
  const isApproved = visit.approval_status?.toLowerCase() === "approved";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm space-y-1.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="size-4 text-gray-400" />
          <p className="text-sm font-medium text-gray-900">{visit.org_name}</p>
        </div>
        <span
          className={`flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full shrink-0 ${
            isApproved
              ? "bg-emerald-50 text-emerald-600"
              : "bg-amber-50 text-amber-600"
          }`}
        >
          {isApproved ? (
            <CheckCircle2 className="size-3" />
          ) : (
            <Circle className="size-3" />
          )}
          {visit.approval_status}
        </span>
      </div>
      <p className="text-xs text-gray-500">{visit.purpose_of_visit}</p>
      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-400 pt-1">
        <span className="flex items-center gap-1">
          <MapPin className="size-3" />
          {visit.location}
        </span>
        <span className="flex items-center gap-1">
          <Phone className="size-3" />
          {visit.contact_number} · {visit.contact_person_designation}
        </span>
        <span className="flex items-center gap-1">
          <Calendar className="size-3" />
          {formatDate(visit.health_awareness_date)}
        </span>
      </div>
    </motion.div>
  );
};

const DailyTaskDetailsDwo = ({ task }: { task: DailyTaskDwo }) => {

    const {
      silver_members_registered,
      silver_members_registered_12,
      gold_members_registered,
      gold_members_registered_12,
      platinum_members_registered,
      platinum_members_registered_12,
      star_members_registered,
      star_members_registered_12,
      diamond_members_registered,
      diamond_members_registered_12,
    } = task;

    const subs = {
      silver_members_registered,
      silver_members_registered_12,
      gold_members_registered,
      gold_members_registered_12,
      platinum_members_registered,
      platinum_members_registered_12,
      star_members_registered,
      star_members_registered_12,
      diamond_members_registered,
      diamond_members_registered_12,
    };

    const weightage = useCalcWeitage(subs);

  return (
    <div className="space-y-6 p-4 rounded-xl border">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            {formatDate(task.date)}
          </p>
        </div>
      </div>

      {/* Top-line stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatTile
          label="Awareness Sessions"
          value={task.health_awareness_sessions}
          icon={ClipboardList}
        />
        <StatTile
          label="Interested Persons"
          value={task.interested_persons}
          icon={Users}
        />
        <StatTile
          label="Individuals Registered"
          value={task.total_registered}
          icon={CheckCircle2}
        />
        <StatTile
          label="Health Checkups"
          value={task.health_checkup_conducted}
          icon={Stethoscope}
        />
        <StatTile
          label="Health Reports Updated"
          value={task.health_reports_added}
          icon={Stethoscope}
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
          <p className="text-[11px] text-gray-400">Total Member Weightage</p>
          <p className="text-sm font-semibold text-gray-900 mt-1">
            {weightage}
          </p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
          <p className="text-[11px] text-gray-400">Supplements Order Amount</p>
          <p className="text-sm font-semibold text-gray-900 mt-1">
            {task.supplements_order_amount}
          </p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
          <p className="text-[11px] text-gray-400">Follow-ups Completed</p>
          <p className="text-sm font-semibold text-gray-900 mt-1">
            {task.followups_completed}
          </p>
        </div>
      </div>

      {/* Membership registrations */}
      <MembershipBreakdown task={task} />

      {/* Org visits */}
      {task.org_visits.length > 0 && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">
            Organization Visits ({task.org_visits.length})
          </p>
          <div className="grid gap-3">
            {task.org_visits.map((visit) => (
              <OrgVisitCard key={visit.id} visit={visit} />
            ))}
          </div>
        </div>
      )}

      {/* Follow-ups */}
      {task.followups.length > 0 && (
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">
            Follow-ups ({task.followups.length})
          </p>
          <div className="grid gap-3">
            {task.followups.map((followup) => (
              <FollowupCard key={followup.id} followup={followup} />
            ))}
          </div>
        </div>
      )}

      {/* Remark */}
      {task.daily_remark && (
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1">
            Daily Remark
          </p>
          <p className="text-sm text-gray-600">{task.daily_remark}</p>
        </div>
      )}
    </div>
  );
};

export default DailyTaskDetailsDwo;
